import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import {
  SecondaryCriteriaTemplate,
  SecondaryCriteria,
  ProjectDetailsConfiguration,
} from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { DragAndDrop, Draggable, DropBox } from "@common/dragAndDrop";
import { CrossIcon } from "@common/crossIcon";
import { Props } from "./types";
import { ColumnIcon } from "./columnIcon";
import { Form } from "./form";
import styles from "./styles.scss";

const fixedColumns = [
  "zone",
  "weather",
  "strategicScore",
  "activityMonitoring",
  "projectManagers",
  // "activityMonitoring_done",
  // "activityMonitoring_delayed",
  // "activityMonitoring_opened",
  "workload",
  // "workload_allocatedTime",
  // "workload_spentTime",
  // "workload_remainingWork",
  "budget",
  // "budget_plannedBudget",
  // "budget_realCosts",
  // "budget_acquiredValue",
];

function hasAvailableOptions(
  template: SecondaryCriteriaTemplate,
  profile: ProjectDetailsConfiguration[number],
) {
  return ![...template.map(({ uuid }) => uuid), ...fixedColumns].every(
    (key) => !!profile.elements.find((column) => column.key === key),
  );
}

function Columns({ project, formData, edit }: Props) {
  const { t, i18n } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "project",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project ? project.uuid : "",
    "project",
  );
  const template = [...planzoneTemplate, ...projectTemplate];
  const validKeySet = [...fixedColumns, ...template.map(({ uuid }) => uuid)];
  const isVisible = (key: string) => !!validKeySet.find((k) => k === key);

  const hiddenByAnchor = (() => {
    const map = new Map<string, typeof formData.elements>();
    let anchor = "__start__";
    formData.elements.forEach((el) => {
      if (isVisible(el.key)) {
        anchor = el.key;
      } else {
        map.set(anchor, [...(map.get(anchor) || []), el]);
      }
    });
    return map;
  })();

  const safeEdit = (profile: typeof formData) => {
    const ordered: typeof formData.elements = [];
    (hiddenByAnchor.get("__start__") || []).forEach((el) => ordered.push(el));

    profile.elements
      .filter((el) => isVisible(el.key))
      .forEach((el) => {
        ordered.push(el);
        (hiddenByAnchor.get(el.key) || []).forEach((h) => ordered.push(h));
      });
    const placed = new Set(ordered.map((el) => el.key));
    hiddenByAnchor.forEach((list) =>
      list.forEach((h) => {
        if (!placed.has(h.key)) ordered.push(h);
      }),
    );
    edit({ ...profile, elements: ordered });
  };
  const currentLang =
    ["fr", "en"].find((lang) => new RegExp(lang, "i").test(i18n.language)) ||
    "fr";
  const getName = (key: string, criteria?: SecondaryCriteria) =>
    criteria
      ? criteria.labels[currentLang as keyof typeof criteria.labels]
        ? criteria.labels[currentLang as keyof typeof criteria.labels]
        : criteria.name
      : t(`projectDetailsConfiguration.columnNames.${key}`, key);

  return (
    <DragAndDrop>
      <div className={styles.columns}>
        <div className={styles.row}>
          {formData.elements
            .filter(({ key }) =>
              [...fixedColumns, ...template.map(({ uuid }) => uuid)].find(
                (k) => k === key,
              ),
            )
            .map(({ key }, index, keys) => {
              const criteria = template.find(({ uuid }) => uuid === key);

              return (
                <React.Fragment key={key}>
                  <DropBox
                    acceptedTypes={["MOVE_COLUMN"]}
                    horizontalSlop={20}
                    verticalSlop={20}
                    drop={({ payload }) => {
                      const droppedIndex = keys.findIndex(
                        ({ key }) => key === payload,
                      );
                      const nextColumns = [
                        ...keys.slice(0, index),
                        keys[droppedIndex],
                        keys[index],
                        ...keys.slice(index + 1),
                      ];

                      safeEdit({
                        ...formData,
                        elements: [
                          ...nextColumns.slice(
                            0,
                            droppedIndex + (index < droppedIndex ? 1 : 0),
                          ),
                          ...nextColumns.slice(
                            droppedIndex + (index < droppedIndex ? 2 : 1),
                          ),
                        ],
                      });
                    }}
                  >
                    {({ payload }, canDrop) => {
                      const criteria = template.find(
                        ({ uuid }) => uuid === payload,
                      );

                      return (
                        canDrop && (
                          <div className={styles.column}>
                            <ColumnIcon
                              project={project}
                              criteriaKey={payload}
                              className={classNames(styles.columnIcon, {
                                [styles.timeSheetIcon]: new RegExp(
                                  "activityMonitoring",
                                  "i",
                                ).test(payload),
                              })}
                            />
                            <p>{getName(payload, criteria)}</p>
                          </div>
                        )
                      );
                    }}
                  </DropBox>
                  <Draggable
                    type="MOVE_COLUMN"
                    className={styles.columnWrapper}
                    data={key}
                    draggedClassName={styles.hidden}
                  >
                    <div className={styles.column}>
                      <ColumnIcon
                        project={project}
                        criteriaKey={key}
                        className={classNames(styles.columnIcon, {
                          [styles.timeSheetIcon]: new RegExp(
                            "activityMonitoring",
                            "i",
                          ).test(key),
                        })}
                      />
                      <p>{getName(key, criteria)}</p>
                      <div
                        className={styles.iconBox}
                        onClick={() =>
                          safeEdit({
                            ...formData,
                            elements: [
                              ...keys.slice(0, index),
                              ...keys.slice(index + 1),
                            ],
                          })
                        }
                      >
                        <CrossIcon className={styles.icon} />
                      </div>
                    </div>
                  </Draggable>
                  <DropBox
                    acceptedTypes={["MOVE_COLUMN"]}
                    horizontalSlop={20}
                    verticalSlop={20}
                    drop={({ payload }) => {
                      const droppedIndex = keys.findIndex(
                        ({ key }) => key === payload,
                      );
                      const nextColumns = [
                        ...keys.slice(0, index),
                        keys[index],
                        keys[droppedIndex],
                        ...keys.slice(index + 1),
                      ];

                      safeEdit({
                        ...formData,
                        elements: [
                          ...nextColumns.slice(
                            0,
                            droppedIndex + (index < droppedIndex ? 1 : 0),
                          ),
                          ...nextColumns.slice(
                            droppedIndex + (index < droppedIndex ? 2 : 1),
                          ),
                        ],
                      });
                    }}
                  >
                    {({ payload }, canDrop) => {
                      const criteria = template.find(
                        ({ uuid }) => uuid === payload,
                      );

                      return (
                        canDrop && (
                          <div className={styles.column}>
                            <ColumnIcon
                              project={project}
                              criteriaKey={payload}
                              className={classNames(styles.columnIcon, {
                                [styles.timeSheetIcon]: new RegExp(
                                  "activityMonitoring",
                                  "i",
                                ).test(payload),
                              })}
                            />
                            <p>{getName(payload, criteria)}</p>
                          </div>
                        )
                      );
                    }}
                  </DropBox>
                </React.Fragment>
              );
            })}
        </div>
        {hasAvailableOptions(template, formData) && (
          <div className={styles.columnWrapper}>
            <Form
              lang={currentLang as "fr" | "en"}
              project={project}
              formData={formData}
              edit={edit}
            />
          </div>
        )}
      </div>
    </DragAndDrop>
  );
}

export { Columns };
